import {
  FC,
  MouseEvent,
  ReactElement,
  useCallback,
  useMemo,
  useState,
} from "react";
import { Box, Button, Divider, Stack, Typography, FormControl, Select, MenuItem } from "@mui/material";

import { filter_black_icon } from "@/constants";
import { DateRangeFilter, DialogMenu } from "@/components";

export const initialFilters = {
  adjustmentType: "" as "" | "value_adjustment" | "duty_rate_adjustment" | "combined_value_and_duty",
  startDate: new Date("1 Jan 2000"),
  endDate: new Date(),
};

type Filters = {
  adjustmentType: "" | "value_adjustment" | "duty_rate_adjustment" | "combined_value_and_duty";
  startDate: Date;
  endDate: Date;
};

const Fiters: FC<{ onFiltersChange: (filters: Filters) => void }> = ({
  onFiltersChange,
}): ReactElement => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const [filters, setFilters] = useState<Filters>(initialFilters);

  const handleAdjustmentTypeChange = useCallback(
    (adjustmentType: "" | "value_adjustment" | "duty_rate_adjustment" | "combined_value_and_duty") => {
      setFilters({
        ...filters,
        adjustmentType,
      });
    },
    [filters]
  );

  const handleRangeDate = useCallback(
    (startDate?: Date, endDate?: Date) => {
      if (startDate && endDate) {
        setFilters({
          ...filters,
          startDate,
          endDate,
        });
      }
    },
    [filters]
  );

  const openFilterModal = useCallback(
    (event: MouseEvent<HTMLButtonElement>): void => {
      setAnchorEl(event.currentTarget);
    },
    []
  );

  const closeFilterModal = useCallback((): void => {
    setAnchorEl(null);
  }, []);

  const applyFilters = (): void => {
    onFiltersChange(filters);
    closeFilterModal();
  };

  const resetFilters = useCallback((): void => {
    setFilters(initialFilters);
  }, []);

  const appliedFilterLength = useMemo(() => {
    let appliedFiltersCount = 0;

    if (filters.adjustmentType !== initialFilters.adjustmentType) {
      appliedFiltersCount++;
    }

    if (
      filters.startDate !== initialFilters.startDate ||
      filters.endDate !== initialFilters.endDate
    ) {
      appliedFiltersCount++;
    }

    return appliedFiltersCount;
  }, [filters]);

  return (
    <>
      <Button
        variant="white-contained"
        onClick={openFilterModal}
        id="advance-filter-anchor"
        aria-controls={anchorEl ? "post-import-filters-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={anchorEl ? "true" : undefined}
        sx={{
          flex: {
            xs: 1,
            sm: "initial",
          },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <img src={filter_black_icon} alt="filter-icon" />
          <Typography variant="body1" color="#052223" fontWeight={500}>
            Filter
          </Typography>
          {appliedFilterLength > 0 && (
            <Box
              sx={{
                width: 24,
                height: 24,
                background: "#0B494B26",
                display: "grid",
                placeContent: "center",
                borderRadius: "50%",
              }}
            >
              <Typography variant="caption" color="#052223" fontWeight={500}>
                {appliedFilterLength}
              </Typography>
            </Box>
          )}
        </Stack>
      </Button>
      <DialogMenu
        id="advance-filter-menu"
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={closeFilterModal}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          p={2.5}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <img src={filter_black_icon} alt="filter" />
            <Typography
              variant="h5"
              fontSize={18}
              fontWeight={600}
              color="#030712"
            >
              Filter
            </Typography>
            <Box
              sx={{
                width: 24,
                height: 24,
                display: "grid",
                placeContent: "center",
                backgroundColor: "error.main",
                color: "white",
                borderRadius: "50%",
              }}
            >
              <Typography variant="caption" fontWeight={500} color="white">
                {appliedFilterLength}
              </Typography>
            </Box>
          </Stack>

          <Typography
            sx={{
              color: "#116B6F",
              textDecorationLine: "underline",
              cursor: "pointer",
            }}
            variant="body2"
            fontWeight={500}
            onClick={resetFilters}
          >
            Clear All
          </Typography>
        </Stack>
        <Divider />
        <Stack sx={{ p: 2 }} spacing={2}>
          <Box>
            <Typography
              sx={{
                mb: 1,
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                color: "#030712",
              }}
              variant="subtitle2"
            >
              Adjustment Type
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                value={filters.adjustmentType}
                onChange={(e) => handleAdjustmentTypeChange(e.target.value as any)}
                displayEmpty
                sx={{
                  backgroundColor: "#fff",
                  "& .MuiSelect-select": {
                    py: 1,
                  },
                }}
              >
                <MenuItem value="">
                  <em>All Adjustment Types</em>
                </MenuItem>
                <MenuItem value="value_adjustment">Value Adjustment</MenuItem>
                <MenuItem value="duty_rate_adjustment">Duty Rate Adjustment</MenuItem>
                <MenuItem value="combined_value_and_duty">Combined Value and Duty Adjustment</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box>
            <Typography
              sx={{
                mb: 1,
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                color: "#030712",
              }}
              variant="subtitle2"
            >
              Date & Time
            </Typography>
            <DateRangeFilter
              value={{
                startDate: filters.startDate,
                endDate: filters.endDate,
              }}
              onSelectDate={({ startDate, endDate }) =>
                handleRangeDate(startDate, endDate)
              }
            />
          </Box>
        </Stack>
        <Divider />
        <Stack
          direction="row"
          justifyContent="space-between"
          spacing={1}
          p={2}
          sx={{
            background: "#F7F8F8",
          }}
        >
          <Button
            variant="white-outlined"
            sx={{ flex: 1 }}
            onClick={closeFilterModal}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{ flex: 1, gap: 1 }}
            onClick={applyFilters}
          >
            Apply
          </Button>
        </Stack>
      </DialogMenu>
    </>
  );
};

export default Fiters;
